import { NotificationCheckIconRaw } from '@tableau/icons/NotificationCheckIcon/raw';
import * as TypeStyle from 'typestyle';
import { encodeSvg } from '../../icons/Icons';
import { ListAndMenuColors } from '../../styles/Colors';
import { AlignItemsCenter, DisplayFlex, FlexDirectionColumn, FlexDirectionColumnReverse, FlexGrow1 } from '../../styles/Flexbox';
import { DefaultFocusIndicator } from '../../styles/FocusIndicators';
import { PreventUserSelect } from '../../styles/PreventUserSelect';
import { TabStyles } from '../../styles/TabStyles';
import { TextEllipsis } from '../../styles/TextEllipsis';

const { Colors, Spacing, Typography } = TabStyles;

export const ListAndMenuPadding = 6;
export const ListAndMenuItemHeight = 24;
const MenuItemCheck = (fill: string = ListAndMenuColors.Text): string => encodeSvg(NotificationCheckIconRaw(12, fill));
MenuItemCheck.size = 12;

const InternalIconPadding = 1; // svg includes 1px of padding
export const ListIsActiveClass = 'listIsActive';
export const ListStyleName = TypeStyle.style(
  PreventUserSelect,
  {
    boxSizing: 'border-box',
    color: ListAndMenuColors.Text,
    fontSize: Typography.Sizing.Default,
    lineHeight: 'normal',
    overflowY: 'auto',
    padding: ListAndMenuPadding,
    '-webkit-overflow-scrolling': 'touch',
    $nest: {
      '&:focus': { outline: 'none' },
      '&::-moz-focus-inner': { $unique: true, border: 'none' },
      '&:focus-visible': DefaultFocusIndicator(),

      '& [role="separator"]': {
        background: Colors.F3,
        height: 1,
        margin: '8px 0',
      },
      '& [role="group"] > :first-child': {
        ...AlignItemsCenter,
        ...DisplayFlex,
        fontWeight: 'bold',
        minHeight: ListAndMenuItemHeight,
      },
      '& [role="option"]': {
        ...AlignItemsCenter,
        ...DisplayFlex,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: `${Spacing.DefaultSpace - InternalIconPadding}px ${(ListAndMenuItemHeight - MenuItemCheck.size) / 2}px`,
        borderRadius: 2,
        minHeight: ListAndMenuItemHeight,
        paddingLeft: MenuItemCheck.size - InternalIconPadding + (Spacing.DefaultSpace * 2),
        paddingRight: Spacing.DefaultSpace,
        $nest: {
          '&[aria-checked="true"]': { backgroundImage: `url('${MenuItemCheck()}')` },
          '&:hover': {
            backgroundColor: ListAndMenuColors.Hover,
            outline: `1px solid ${ListAndMenuColors.ActiveBorder}`,
            outlineOffset: -1,
          },
          '&[aria-disabled="true"]': {
            backgroundColor: 'transparent',
            color: ListAndMenuColors.DisabledTextWithOpacity,
            outlineColor: 'transparent',
            opacity: ListAndMenuColors.Opacity,
          },
        }
      },
      // the selector for pressed state needs to be at least the same specificity as the selector for aria-selected in order to override properly
      '&& [role="option"]:active': {
        backgroundColor: ListAndMenuColors.Press,
        outline: `1px solid ${ListAndMenuColors.ActiveBorder}`,
        outlineOffset: -1,
      },
      [`&:focus [role="option"][aria-selected="true"], &.${ListIsActiveClass} [role="option"][aria-selected="true"]`]: {
        backgroundColor: ListAndMenuColors.Active, // (ab)use aria-selected as "focus"
        outline: `1px solid ${ListAndMenuColors.ActiveBorder}`,
        outlineOffset: -1,
      },
      // eslint-disable-next-line max-len
      [`&:focus [role="option"][aria-selected="true"][aria-disabled="true"], &.${ListIsActiveClass} [role="option"][aria-selected="true"][aria-disabled="true"]`]: {
        backgroundColor: 'transparent !important',
        outline: `1px solid ${ListAndMenuColors.DisabledBorder} !important`,
      },
    }
  },
);

export const EllipsedStyleName = TypeStyle.style(FlexGrow1, TextEllipsis);

export const FilteringListStyleName = TypeStyle.style(
  {
    ...DisplayFlex,
    ...FlexDirectionColumn,
    boxSizing: 'border-box',
    lineHeight: 'normal',
    $nest: {
      '&.reverse': {
        ...FlexDirectionColumnReverse,
      },
      '&:focus-visible, & :focus-visible': DefaultFocusIndicator(),
    },
  },
);
